import { useState, useEffect, useCallback, useMemo } from "react";
import { save } from "../../shared/stamps";

/**
 * Cards you have sent to be graded, and whether it was worth it.
 *
 * The centering screen answers "should I send this?" before the fact. This is
 * the same question afterwards and with real numbers in it: what the raw card
 * cost, what grading cost, what it came back as, and what it sold for. Those
 * four are the whole thing — everything else here is arithmetic on them.
 *
 * Money is held in whole pounds and pence as typed. No currency conversion, no
 * rounding on the way in: a total that disagrees with the receipts by a penny
 * is a total nobody trusts.
 */

const KEY = "ppt_submissions_v1";

export const STATUSES = [
  { id: "bought", label: "Bought", hint: "Raw, not sent yet" },
  { id: "sent", label: "Sent", hint: "With the grader" },
  { id: "back", label: "Back", hint: "Graded, not sold" },
  { id: "sold", label: "Sold", hint: "Done" },
];

export const STATUS_IDS = STATUSES.map((s) => s.id);

/**
 * Where a card is, worked out rather than chosen.
 *
 * Three of the four stages were already written down elsewhere on the row: a
 * grade means it is back, a sale means it is sold. Asking for them again was a
 * dropdown that could disagree with the numbers beside it — and a row saying
 * "Bought" next to a sale price is worse than no stage at all. The one thing
 * that is not derivable is whether it has actually gone, so that is the only
 * thing still asked for.
 */
export function stageOf(entry) {
  if (num(entry?.sold) != null) return "sold";
  if (String(entry?.grade ?? "").trim()) return "back";
  return entry?.sent ? "sent" : "bought";
}

/* PSA's own submission tiers, so the grading cost is picked rather than
 * remembered. The Value tiers are left out because PSA has them paused.
 *
 * These prices move, and this list is a snapshot rather than a feed — the
 * amount lands in an ordinary box you can type over, and the screen says to
 * check PSA before trusting it. Getting this wrong quietly is how a profit
 * column ends up flattering.
 */
export const PSA_TIERS = [
  { id: "regular", label: "Regular", price: 79.99 },
  { id: "express", label: "Express", price: 149.99 },
  { id: "superexpress", label: "Super Express", price: 299.99 },
];

// PSA grades in the halves it actually issues, best first.
export const GRADES = [
  "10", "9.5", "9", "8.5", "8", "7.5", "7", "6.5", "6",
  "5.5", "5", "4.5", "4", "3.5", "3", "2.5", "2", "1.5", "1",
];

/* A log holds both games at once, the way the collection does. The game is not
   there to make the name field work — that was always free text and always
   took an OP code fine — it is there so the totals can answer whether grading
   One Piece is paying, which is a different question from whether grading is
   paying, and one this app cannot answer any other way: One Piece has no price
   source in it at all. */
export const GAME_LABEL = { pokemon: "Pokémon", onepiece: "One Piece" };
export const GAME_IDS = Object.keys(GAME_LABEL);
const asGame = (g) => (GAME_IDS.includes(g) ? g : "pokemon");

const newId = () => `s${Date.now()}${Math.random().toString(36).slice(2, 6)}`;

/** Blank rather than zero. A cost you have not entered is not a cost of nothing,
 *  and starting the boxes at 0 makes every unfilled row look paid for. */
const num = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * What one card cost, made, and cleared.
 *
 * Fees come off the sale before anything else, because that is the order the
 * money actually moves in — you never see the marketplace's cut.
 */
export function tally(entry) {
  const paid = num(entry.paid) ?? 0;
  const grading = num(entry.grading) ?? 0;
  /* Everything else that hit this card. A submission is not only the fee: PSA
     upcharge a card that comes back worth more than the tier it was declared
     at, postage goes both ways and is paid per batch rather than per card, and
     the tax on the fee is real money too.
     One box rather than five. They arrive at different times and from
     different places, but they are all the same thing here — money spent to
     get this card graded — and a row of half-empty boxes would be a worse
     record than one honest number with a note beside it. */
  const other = num(entry.other) ?? 0;
  const spent = paid + grading + other;
  const feePct = num(entry.feePct) ?? 0;

  /* What it is worth today, if it has been looked up. Kept apart from the sale
     all the way through: an estimate that can quietly become a receipt is the
     one thing this screen must not allow. */
  const value = num(entry.value);
  const estimate =
    value == null ? null : { net: value * (1 - feePct / 100), profit: value * (1 - feePct / 100) - spent };

  const sold = num(entry.sold);
  if (sold == null) return { spent, net: null, profit: null, sold: null, estimate };

  const net = sold * (1 - feePct / 100);
  return { spent, net, profit: net - spent, sold, estimate };
}

export default function useSubmissions() {
  const [entries, setEntries] = useState([]);
  /* State, not a ref — the same trap the lists fell into: a ref flips inside
     the load effect and the save effect runs in that same commit still holding
     the empty array, writing it over everything saved. */
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setEntries(parsed);
      }
    } catch {
      /* an unreadable log starts empty rather than breaking the screen */
    }
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      save(KEY, JSON.stringify(entries));
    } catch {
      /* still usable this session */
    }
  }, [entries, ready]);

  const add = useCallback((entry) => {
    const made = {
      id: newId(),
      name: "",
      set: "",
      number: "",
      paid: "",
      grading: "",
      other: "",
      sold: "",
      feePct: "",
      grade: "",
      sent: false,
      note: "",
      addedAt: new Date().toISOString(),
      ...entry,
      // After the spread, and the only place it is set: asGame already falls
      // back to Pokemon, so a default above it would just be a duplicate key.
      game: asGame(entry?.game),
    };
    setEntries((prev) => [made, ...prev]);
    return made.id;
  }, []);

  const update = useCallback((id, patch) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  /** @returns { entry, index } for taking it back, or null. */
  const remove = useCallback((id) => {
    const index = entries.findIndex((e) => e.id === id);
    if (index < 0) return null;
    const entry = entries[index];
    setEntries((prev) => prev.filter((e) => e.id !== id));
    return { entry, index };
  }, [entries]);

  /** Back where it was, as it was — a submission row is a record of money. */
  const restore = useCallback((entry, index) => {
    setEntries((prev) => {
      if (prev.some((e) => e.id === entry.id)) return prev;
      const at = Math.min(Math.max(0, index), prev.length);
      return [...prev.slice(0, at), entry, ...prev.slice(at)];
    });
  }, []);

  /**
   * The numbers for the whole log.
   *
   * Sold and unsold are kept apart on purpose. Rolling them together produces
   * a profit figure that improves every time you spend money, because the
   * cards still with the grader have costs and no revenue yet — which is the
   * most flattering possible way to be wrong about whether this is working.
   */
  const totals = useMemo(() => {
    const blank = () => ({ spent: 0, outstanding: 0, revenue: 0, profit: 0, soldCount: 0, count: 0 });
    const all = blank();
    const byGame = {};

    for (const e of entries) {
      const t = tally(e);
      const g = asGame(e.game);
      if (!byGame[g]) byGame[g] = blank();
      for (const bucket of [all, byGame[g]]) {
        bucket.count += 1;
        bucket.spent += t.spent;
        if (t.profit == null) {
          bucket.outstanding += t.spent;
        } else {
          bucket.revenue += t.net;
          bucket.profit += t.profit;
          bucket.soldCount += 1;
        }
      }
    }
    for (const b of [all, ...Object.values(byGame)]) b.openCount = b.count - b.soldCount;
    return { ...all, byGame };
  }, [entries]);

  return { entries, ready, add, update, remove, restore, totals };
}
